import { LazyImage } from '@/components/ui/LazyImage';
import type { BusinessProduct, SectionProps } from '@/types';

function formatPrice(price: number | null, currency: string): string {
  if (price === null) return 'Price on request';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(price);
}

export function ProductsSection({ business, config }: SectionProps) {
  const title = (config.title as string) || (business.site_type === 'menu' ? 'Menu' : 'Products & services');
  const products = business.products;

  if (!products.length) return null;

  if (business.site_type === 'menu') {
    return <MenuListView title={title} products={products} />;
  }

  const columns = (config.columns as number) || 3;
  const gridClass =
    columns === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : columns === 4
        ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="section-card">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{products.length} items available</p>

      <div className={`mt-6 grid gap-4 ${gridClass}`}>
        {products.map((product) => (
          <article
            key={product.id}
            className="group overflow-hidden rounded-brand border border-slate-200 bg-white transition hover:shadow-md"
          >
            {product.image_url ? (
              <LazyImage
                src={product.image_url}
                alt={product.name}
                aspectRatio="aspect-square"
                className="w-full"
              />
            ) : (
              <div className="flex aspect-square items-center justify-center bg-slate-100 text-slate-400">
                <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            )}
            <div className="p-4">
              <h3 className="font-medium text-slate-900 group-hover:text-brand-primary">
                {product.name}
              </h3>
              {product.description && (
                <p className="mt-1 line-clamp-2 text-sm text-slate-500">{product.description}</p>
              )}
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-lg font-semibold text-brand-primary">
                  {formatPrice(product.price, product.currency)}
                </span>
                {product.compare_at_price && product.price && product.compare_at_price > product.price && (
                  <span className="text-sm text-slate-400 line-through">
                    {formatPrice(product.compare_at_price, product.currency)}
                  </span>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/** Text-only menu listing (name + price row) — no per-item images, on a
 * subtly decorative background. Used for `site_type === 'menu'`. */
function MenuListView({ title, products }: { title: string; products: BusinessProduct[] }) {
  return (
    <section className="section-card relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, currentColor 1px, transparent 1px), radial-gradient(circle at 80% 60%, currentColor 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="relative">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{products.length} items available</p>

        <ul className="mt-6 divide-y divide-dashed divide-slate-300">
          {products.map((product) => (
            <li key={product.id} className="flex items-baseline justify-between gap-4 py-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{product.name}</p>
                {product.description && (
                  <p className="mt-0.5 line-clamp-1 text-sm text-slate-500">{product.description}</p>
                )}
              </div>
              <span className="shrink-0 font-semibold text-brand-primary">
                {formatPrice(product.price, product.currency)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
